import type { ApolloError } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@cmg/design-system';
import {
  FormikAutocompleteField,
  FormikSelectField,
  FormikTextField,
} from '@cmg/design-system-formik';
import type { FormikHelpers } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import type { AnyObject, Maybe, ObjectSchema } from 'yup';

import { GQLExtensionErrorAlert } from '../../../../../../../../../design-system/alert/GQLExtensionErrorAlert';
import type { UpdateInvestorFirmEmployeeInput } from '../../../../../../../../../graphql';
import { EMPTY_OPTIONS, STATUS_OPTIONS } from './InvestorContactDialog.model';

export enum DialogMode {
  Create,
  Edit,
}

const DIALOG_TITLE = {
  [DialogMode.Create]: 'New Investor Contact',
  [DialogMode.Edit]: 'Edit Investor Contact',
};

const DIALOG_ACTION_LABEL = {
  [DialogMode.Create]: 'Add',
  [DialogMode.Edit]: 'Save',
};

export type InvestorContactDialogProps<TValues> = Readonly<{
  mode: DialogMode;
  open: boolean;
  error?: ApolloError;
  initialValues: TValues;
  validationSchema: ObjectSchema<TValues extends Maybe<AnyObject> ? TValues : AnyObject>;
  onClose: () => void;
  onSubmit: (values: TValues) => Promise<void>;
}>;

export function InvestorContactDialog<TValues extends UpdateInvestorFirmEmployeeInput>({
  mode,
  open,
  error,
  initialValues,
  validationSchema,
  onClose,
  onSubmit,
}: InvestorContactDialogProps<TValues>) {
  const handleSubmit = useCallback<
    (values: TValues, formikHelpers: FormikHelpers<TValues>) => Promise<void>
  >(
    async (values, formikHelpers) => {
      await onSubmit(values);
      formikHelpers.resetForm();
    },
    [onSubmit]
  );

  const formik = useFormik<TValues>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCancel = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [onClose, formik]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <Dialog fullWidth maxWidth="md" open={open}>
      <DialogTitle>{DIALOG_TITLE[mode]}</DialogTitle>
      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Stack spacing={4}>
            <Stack spacing={2}>
              {error && (
                <GQLExtensionErrorAlert error={error} title="Failed to save investor contact" />
              )}
              <Stack spacing={2} direction="row">
                <FormikTextField
                  name="employeeKey"
                  label="Employee Key"
                  required
                  disabled={mode === DialogMode.Edit}
                />
                <FormikSelectField
                  name="status"
                  label="Status"
                  options={STATUS_OPTIONS}
                  fullWidth
                />
              </Stack>
              <Stack spacing={2} direction="row">
                <FormikTextField name="name" label="Contact Name" required />
                <FormikAutocompleteField
                  name="titles"
                  label="Title(s)"
                  options={EMPTY_OPTIONS}
                  multiple
                  freeSolo
                />
              </Stack>
              <Stack spacing={2} direction="row">
                <FormikTextField name="email1" label="Email Address (Primary)" required />
                <FormikTextField name="email2" label="Email Address (Secondary)" />
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack spacing={2} direction="row">
                <FormikTextField name="phoneNumber1" label="Phone (Primary)" />
                <FormikTextField name="phoneNumber2" label="Phone (Secondary)" />
              </Stack>
              <Stack spacing={2} direction="row">
                <FormikTextField name="address1" label="Address (Primary)" />
                <FormikTextField name="address2" label="Address (Secondary)" />
              </Stack>
              <Stack spacing={2} direction="row">
                <FormikTextField name="faxNumber1" label="Fax (Primary)" />
                <FormikTextField name="faxNumber2" label="Fax (Secondary)" />
              </Stack>
            </Stack>
          </Stack>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {DIALOG_ACTION_LABEL[mode]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
